import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Grid,
  Typography,
  useMediaQuery,
  Card,
  CardContent,
  IconButton,
  Skeleton,
  Paper,
  Fade,
  Stack,
  alpha,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useUserAuth } from "../../context/UserAuthContext";
import theme from "../../styles/styleTheme";
import { useTranslation } from "react-i18next";
import ApiFetch from "../../services/api.fetch";
import { URLS } from "../../utils/url";
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { Modal } from "../common/Modal";
import CreateProfileModal from "../ScreeningProfiles/CreateProfileModal";

interface ScreeningProfile {
  profile: {
    id: number;
    tenantId: number;
    createdTimestamp: string;
    updatedTimestamp: string;
    employmentType: string;
    screeningType: string;
    firstname: string;
    initials: string;
    surname: string;
    birthdate: string;
    birthplace: string;
    province: string;
    street: string;
    houseNumber: string;
    postalCode: string;
    city: string;
    country: string;
    phoneNumber: string;
    email: string;
    income: number;
  };
  documents: any[];
}

const ScreeningProfiles = () => {
  const { user } = useUserAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [profiles, setProfiles] = useState<ScreeningProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [profileToDelete, setProfileToDelete] = useState<number | null>(null);
  const [createProfileModalOpen, setCreateProfileModalOpen] = useState(false);

  useEffect(() => {
    document.title = `${t("profile.screening_profile")} - GoHome.rent`;
    fetchProfiles();
  }, []);

  const fetchProfiles = async () => {
    try {
      if (!user?.tenant?.id) return;
      const response = await ApiFetch.httpGet(
        URLS.get.tenant.profile.profilesByTenantId(user.tenant.id)
      );
      setProfiles(response);
    } catch (err) {
      setError("Failed to fetch GoHome ID's");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditProfile = (profileId: number) => {
    navigate(`/screening-profile/${profileId}`);
  };

  const handleAddProfile = () => {
    setCreateProfileModalOpen(true);
  };

  const handleCreateProfileSuccess = (profileId: number) => {
    navigate(`/screening-profile/${profileId}`);
  };

  const handleDeleteProfile = async (profileId: number) => {
    try {
      await ApiFetch.httpDelete(`/v1/tenant/profile/deleteProfile/${profileId}`, {});
      setProfiles(profiles.filter(profile => profile.profile.id !== profileId));
      setDeleteModalOpen(false);
      setProfileToDelete(null);
    } catch (err) {
      setError("Failed to delete screening profile");
      console.error(err);
    }
  };

  const handleDeleteClick = (profileId: number) => {
    setProfileToDelete(profileId);
    setDeleteModalOpen(true);
  };

  const LoadingSkeleton = () => (
    <Grid item xs={12} sm={6} md={4}>
      <Card sx={{ 
        height: '100%',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <CardContent sx={{ 
          p: 2,
          pr: 6,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center'
        }}>
          <Skeleton variant="text" width="60%" height={24} />
        </CardContent>
        <Box 
          sx={{ 
            position: 'absolute',
            right: 0,
            top: 0,
            bottom: 0,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 0.5,
            pr: 0.5
          }}
        >
          <Skeleton variant="rectangular" width={32} height={32} sx={{ borderRadius: '4px' }} />
          <Skeleton variant="rectangular" width={32} height={32} sx={{ borderRadius: '4px' }} />
        </Box>
      </Card>
    </Grid>
  );

  return (
    <Grid
      container
      item
      padding={isSmallScreen ? 1 : 2}
      xs={12}
      display={"flex"}
      alignContent={"flex-start"}
      spacing={1.5}
    >
      <Grid item xs={12}>
        <Paper elevation={0} sx={{ p: 1.5, mb: 1.5, backgroundColor: 'transparent' }}>
          <Box display={"flex"} justifyContent={"space-between"} alignItems={"center"}>
            <Box display={"flex"} alignContent={"center"} gap={1} alignItems={"center"}>
              <Box sx={{ 
                backgroundColor: 'primary.main', 
                borderRadius: '8px', 
                p: 0.75,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <FingerprintIcon sx={{ color: 'white', fontSize: '1.25rem' }} />
              </Box>
              <Typography variant="h4" fontWeight={600} sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem' } }}>
                {t("profile.screening_profile")}
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Grid>

      <Grid item xs={12}>
        <Paper 
          elevation={0} 
          sx={{ 
            p: 2, 
            mb: 2, 
            backgroundColor: alpha(theme.palette.primary.main, 0.04),
            borderRadius: '8px',
            border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`
          }}
        >
          <Stack spacing={1}>
            <Typography variant="subtitle1" fontWeight={600} color="primary">
              {t("screening_profile.what_is_this")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', lineHeight: 1.5 }}>
              {t("screening_profile.description")}
            </Typography>
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary" component="div" sx={{ fontSize: '0.875rem' }}>
                <ul style={{ margin: 0, paddingLeft: '1.5rem' }}>
                  <li style={{ marginBottom: '0.25rem' }}>{t("screening_profile.benefit_1")}</li>
                  <li style={{ marginBottom: '0.25rem' }}>{t("screening_profile.benefit_2")}</li>
                  <li>{t("screening_profile.benefit_3")}</li>
                </ul>
              </Typography>
            </Box>
          </Stack>
        </Paper>
      </Grid>

      <Grid item xs={12}>
        <Box display="flex" justifyContent="flex-start" mb={2}>
          <Button
            variant="contained"
            color="secondary"
            size="small"
            sx={{
              color: "#fff",
              borderRadius: '6px',
              px: 2,
              py: 0.75,
              fontSize: '0.875rem',
              fontWeight: 500,
              '&:hover': {
                transform: 'translateY(-1px)',
                transition: 'transform 0.2s ease-in-out'
              }
            }}
            startIcon={<AddIcon sx={{ fontSize: '1.25rem' }} />}
            onClick={handleAddProfile}
          >
            {t("screening_profile.add_profile")}
          </Button>
        </Box>
      </Grid>

      {loading ? (
        <>
          <LoadingSkeleton />
          <LoadingSkeleton />
          <LoadingSkeleton />
        </>
      ) : error ? (
        <Grid item xs={12}>
          <Paper elevation={0} sx={{ p: 2, textAlign: 'center', backgroundColor: alpha(theme.palette.error.main, 0.08) }}>
            <Typography color="error.main" variant="subtitle1" sx={{ fontSize: '0.875rem' }}>{error}</Typography>
          </Paper>
        </Grid>
      ) : profiles.length === 0 ? (
        <Grid item xs={12}>
          <Paper elevation={0} sx={{ p: 3, textAlign: 'center', backgroundColor: alpha(theme.palette.grey[500], 0.04) }}>
            <Typography variant="subtitle1" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
              {t("screening_profile.no_screening_profiles")}
            </Typography>
          </Paper>
        </Grid>
      ) : (
        profiles.map((profile) => (
          <Grid item xs={12} sm={6} md={4} key={profile.profile.id}>
            <Fade in={true}>
              <Card sx={{
                height: '100%',
                transition: 'all 0.2s ease-in-out',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                }
              }}>
                <CardContent sx={{ 
                  p: 2,
                  pr: 6,
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center'
                }}>
                  <Typography 
                    variant="subtitle1" 
                    sx={{ 
                      fontWeight: 500,
                      fontSize: '0.9375rem',
                      color: 'text.primary'
                    }}
                  >
                    {profile.profile.firstname} {profile.profile.surname}
                  </Typography>
                </CardContent>
                <Box 
                  sx={{ 
                    position: 'absolute',
                    right: 0,
                    top: 0,
                    bottom: 0,
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 0.5,
                    pr: 0.5
                  }}
                >
                  <IconButton 
                    size="small"
                    onClick={() => handleEditProfile(profile.profile.id)}
                    sx={{ 
                      color: '#fff',
                      backgroundColor: theme.palette.secondary.main,
                      p: 0.5,
                      borderRadius: '4px',
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.secondary.main, 0.8)
                      }
                    }}
                  >
                    <EditIcon sx={{ fontSize: '1.125rem' }} />
                  </IconButton>
                  <IconButton 
                    size="small"
                    onClick={() => handleDeleteClick(profile.profile.id)}
                    sx={{ 
                      color: '#fff',
                      backgroundColor: theme.palette.secondary.main,
                      p: 0.5,
                      borderRadius: '4px',
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.secondary.main, 0.8)
                      }
                    }}
                  >
                    <CloseIcon sx={{ fontSize: '1.125rem' }} />
                  </IconButton>
                </Box>
              </Card>
            </Fade>
          </Grid>
        ))
      )}

      <CreateProfileModal
        open={createProfileModalOpen}
        onClose={() => setCreateProfileModalOpen(false)}
        onSuccess={handleCreateProfileSuccess}
      />
      <Modal
        headerCloseButton={true}
        actions={true}
        open={deleteModalOpen}
        setOpen={setDeleteModalOpen}
        title={t("screening_profile.delete_confirm_title")}
        description={t("screening_profile.delete_confirm_description")}
        size="sm"
        closeButtonTitle={t("No")}
        submitButtonTitle={t("Yes")}
        onConfirm={() => profileToDelete && handleDeleteProfile(profileToDelete)}
      />
    </Grid>
  );
};

export default ScreeningProfiles;
